import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Person as PersonIcon
} from "@material-ui/icons";
import { Select } from "antd";
import styled from "styled-components";

import { UnsavedTypeWellChangesPopconfirm } from "components/arps/UnsavedTypeWellChangesPopconfirm";
import { Tooltip } from "components/base";

import { workspaceTypeIcon } from "./WorkspaceTypeDisplay";
import {
  type WorkspaceOption,
  useWorkspaceNavController
} from "./controllers/useWorkspaceNavController";

// ---------------------------------------------------------------------------
// Pure view props
// ---------------------------------------------------------------------------

export interface WorkspaceNavViewProps {
  readonly workspace: Maybe<{
    readonly workspaceId: string;
    readonly type?: Maybe<string>;
  }>;
  readonly options: readonly WorkspaceOption[];
  readonly isPopconfirmOpen: boolean;
  readonly onWorkspaceChange: (workspaceId: string) => void;
  readonly onConfirmChange: () => void;
  readonly onCancelChange: () => void;
}

// ---------------------------------------------------------------------------
// Pure view — no hooks, no store access, no internal state.
// Exported separately so it can be tested in isolation with plain props.
// ---------------------------------------------------------------------------

export function WorkspaceNavView({
  workspace,
  options,
  isPopconfirmOpen,
  onWorkspaceChange,
  onConfirmChange,
  onCancelChange
}: WorkspaceNavViewProps) {
  if (!workspace?.workspaceId) return null;

  // Decorate the plain-data options with icons for rendering. Personal stands in for a missing
  // type because every row needs an icon: the type is nullable on the model, though nothing
  // reaches here without one — WorkspaceGraphType resolves a stored null to Personal before it
  // leaves the service.
  const decoratedOptions = options.map((opt) => ({
    label: (
      <WorkspaceItem>
        {workspaceTypeIcon(opt.type) ?? <PersonIcon />} {opt.label}
      </WorkspaceItem>
    ),
    value: opt.value
  }));

  return (
    <Tooltip placement="bottomRight" title="Choose Workspace">
      <UnsavedTypeWellChangesPopconfirm
        open={isPopconfirmOpen}
        placement={"bottom"}
        onConfirm={onConfirmChange}
        onCancel={onCancelChange}
        data-testid="unsaved-type-well-changes-popconfirm">
        <StyledSelect
          popupClassName="modal-select"
          value={workspace.workspaceId}
          suffixIcon={<KeyboardArrowDownIcon fontSize="large" />}
          options={decoratedOptions}
          onChange={onWorkspaceChange}
        />
      </UnsavedTypeWellChangesPopconfirm>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Connected component — wires the controller to the pure view.
// ---------------------------------------------------------------------------

function WorkspaceNav() {
  const controller = useWorkspaceNavController();

  return (
    <WorkspaceNavView
      workspace={controller.workspace}
      options={controller.options}
      isPopconfirmOpen={controller.isPopconfirmOpen}
      onWorkspaceChange={controller.onWorkspaceChange}
      onConfirmChange={controller.onConfirmChange}
      onCancelChange={controller.onCancelChange}
    />
  );
}

export default WorkspaceNav;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const WorkspaceItem = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  color: var(--color-text);
`;

const StyledSelect = styled(Select)`
  min-width: 0;
  flex-grow: 1;
  margin-right: var(--space-3);

  .ant-select-arrow {
    transform: rotate(270deg);
    transition: all 0.2s;
  }

  &.ant-select-open .ant-select-arrow {
    transform: rotate(360deg);
  }
`;
